import { __awaiter } from 'tslib';
import { ethers, utils } from 'ethers';
import { normalizeWalletName } from '../../utils/normalizeWalletName/normalizeWalletName.js';
import { ProviderLookup } from '../ProviderLookup.js';
import { isUnsupportedProviderError } from '../../utils/isUnsupportedProviderError/isUnsupportedProviderError.js';
import { logger } from '../../utils/logger.js';

class EthProviderHelper {
    constructor() {
        this.MetamaskProvider = () => 
        // Stop these wallets from showing up as MetaMask
        this.installedProviderLookup([
            { flag: 'isDawn', value: false },
            { flag: 'isBraveWallet', value: false },
            { flag: 'isTrustWallet', value: false },
            { flag: 'isExodus', value: false },
            { flag: 'isPhantom', value: false },
            { flag: 'isZerion', value: false },
            { flag: 'isMetaMask', value: true },
        ]);
        this.BraveProvider = () => this.installedProviderLookup([{ flag: 'isBraveWallet', value: true }]);
        this.DawnProvider = () => this.installedProviderLookup([{ flag: 'isDawn', value: true }]);
        this.FrameProvider = () => this.installedProviderLookup([{ flag: 'isFrame', value: true }]);
        this.CoinbaseProvider = () => this.installedProviderLookup([{ flag: 'isCoinbaseWallet', value: true }]);
        this.OperaProvider = () => this.installedProviderLookup([{ flag: 'isOpera', value: true }]);
        this.GameStopProvider = () => this.installedProviderLookup([{ flag: 'isGamestop', value: true }]);
        this.ExodusProvider = () => this.installedProviderLookup([{ flag: 'isExodus', value: true }]);
        this.BloctoProvider = () => this.installedProviderLookup([{ flag: 'isBlocto', value: true }]);
        this.TrustWalletProvider = () => this.installedProviderLookup([{ flag: 'isTrustWallet', value: true }]);
        this.PhantomProvider = () => this.installedProviderLookup([{ flag: 'isPhantom', value: true }]);
        this.ZerionProvider = () => this.installedProviderLookup([{ flag: 'isZerion', value: true }]);
        this.UnknownInjectedProvider = () => this.installedProviderLookup([
            { flag: 'isMetaMask', value: false },
            { flag: 'isExodus', value: false },
            { flag: 'isCoinbaseWallet', value: false },
            { flag: 'isBraveWallet', value: false },
            { flag: 'isGamestop', value: false },
            { flag: 'isFrame', value: false },
            { flag: 'isOpera', value: false },
            { flag: 'isBlocto', value: false },
            { flag: 'isTrustWallet', value: false },
            { flag: 'isPhantom', value: false },
            { flag: 'isZerion', value: false },
        ]);
        this.providers = {
            bloctoinjected: this.BloctoProvider,
            braveevm: this.BraveProvider,
            coinbase: this.CoinbaseProvider,
            dawn: this.DawnProvider,
            exodusevm: this.ExodusProvider,
            frame: this.FrameProvider,
            gamestop: this.GameStopProvider,
            injectedwallet: this.UnknownInjectedProvider,
            metamask: this.MetamaskProvider,
            opera: this.OperaProvider,
            phantomevm: this.PhantomProvider,
            trust: this.TrustWalletProvider,
            zerion: this.ZerionProvider,
        };
    }
    installedProviderLookup(providerFlags) {
        const allInstalledProviders = this.allInstalledProviders();
        return ProviderLookup(allInstalledProviders, providerFlags);
    }
    allInstalledProviders() {
        var _a;
        const ethereumProviders = [];
        if (window.ethereum) {
            if (!window.ethereum.providers) {
                ethereumProviders.push(window.ethereum);
            }
            else {
                window.ethereum.providers.forEach((provider) => {
                    ethereumProviders.push(provider);
                });
            }
        }
        if ((_a = window.phantom) === null || _a === void 0 ? void 0 : _a.ethereum) {
            ethereumProviders.push(window.phantom.ethereum);
        }
        if (window.zerionWallet) {
            ethereumProviders.push(window.zerionWallet);
        }
        return ethereumProviders;
    }
    isInstalledHelper(walletName) {
        return this.findProvider(walletName) !== undefined;
    }
    findProvider(walletName) {
        var _a, _b;
        try {
            const provider = (_b = (_a = this.providers)[normalizeWalletName(walletName)]) === null || _b === void 0 ? void 0 : _b.call(_a);
            return provider === undefined
                ? undefined
                : new ethers.providers.Web3Provider(provider, 'any'); // https://github.com/ethers-io/ethers.js/discussions/1480
        }
        catch (err) {
            if (isUnsupportedProviderError(err)) {
                logger.error(`ETH Provider is not supported ${err.value}`, err);
                return;
            }
            logger.error(`Could not find provider for ${walletName}`, err);
            return undefined;
        }
    }
    fetchPublicAddressWithName(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.findProvider(name);
            if (!provider) {
                return Promise.resolve(undefined);
            }
            return this.fetchPublicAddressWithProvider(provider);
        });
    }
    fetchPublicAddressWithProvider(provider) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const [lowercaseAddress] = yield provider.send('eth_requestAccounts', []);
                const publicAddress = utils.getAddress(lowercaseAddress);
                return publicAddress;
            }
            catch (err) {
                logger.error(err);
                return Promise.reject(err);
            }
        });
    }
    signMessageWithName(messageToSign, name) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletAddress = yield this.fetchPublicAddressWithName(name);
            if (!walletAddress) {
                return Promise.resolve(undefined);
            }
            const provider = this.findProvider(name);
            if (!provider) {
                return Promise.resolve(undefined);
            }
            const signer = provider.getSigner();
            const signedMessage = yield signer.signMessage(messageToSign);
            return signedMessage;
        });
    }
    _setupEventListeners(listeners, web3Provider) {
        if (!web3Provider) {
            return () => {
                // nothing to teardown
            };
        }
        const externalProvider = web3Provider.provider;
        this.handleAccountChange = (accounts) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (accounts.length === 0) {
                yield ((_a = listeners.onDisconnect) === null || _a === void 0 ? void 0 : _a.call(listeners));
                return;
            }
            yield ((_b = listeners.onAccountChange) === null || _b === void 0 ? void 0 : _b.call(listeners, accounts));
        });
        this.handleChainChange = () => __awaiter(this, void 0, void 0, function* () {
            var _c;
            const network = yield web3Provider.getNetwork();
            yield ((_c = listeners.onChainChange) === null || _c === void 0 ? void 0 : _c.call(listeners, network.chainId.toString()));
        });
        this.handleDisconnect = (error) => __awaiter(this, void 0, void 0, function* () {
            var _d;
            if ((error === null || error === void 0 ? void 0 : error.code) === 1013) {
                return;
            }
            yield ((_d = listeners.onDisconnect) === null || _d === void 0 ? void 0 : _d.call(listeners));
        });
        externalProvider.on('accountsChanged', this.handleAccountChange);
        externalProvider.on('chainChanged', this.handleChainChange);
        externalProvider.on('disconnect', this.handleDisconnect);
        return () => { };
    }
    _teardownEventListeners(name) {
        const web3Provider = this.findProvider(name);
        if (!web3Provider || !(web3Provider === null || web3Provider === void 0 ? void 0 : web3Provider.provider)) {
            return;
        }
        const externalProvider = web3Provider.provider;
        if (this.handleAccountChange) {
            externalProvider.removeListener('accountsChanged', this.handleAccountChange);
        }
        if (this.handleChainChange) {
            externalProvider.removeListener('chainChanged', this.handleChainChange);
        }
        if (this.handleDisconnect) {
            externalProvider.removeListener('disconnect', this.handleDisconnect);
        }
    }
}

export { EthProviderHelper };
